import { createContext, useContext, useEffect, useRef, useState } from "react";
import WebFxScan from "../lib/scan.js";
import { clientLog } from "../lib/clientLog.js";
import { api } from "../api";
import { TEST_BUILD } from "../lib/testBuild.js";

const DEFAULT_IP = "127.0.0.1";
const DEFAULT_PORT = "17778";
const CONNECT_TIMEOUT_MS = 10000;
const AUTO_CONNECT_RETRY_MS = 15000;
const HEALTH_CHECK_MS = 60000;
const HEALTH_CHECK_TIMEOUT_MS = 15000;
const IS_ELECTRON = !TEST_BUILD && typeof navigator !== "undefined" && /Electron/.test(navigator.userAgent);
const NO_DEVICES = "LIBWFX_ERRCODE_NO_DEVICES";
// Consecutive failed connects before the agent is restarted: a hung agent fails fast, a missing
// device may just be a scanner that is still powering up.
const AGENT_RESTART_AFTER = 2;
const AGENT_RESTART_AFTER_NO_DEVICES = 4;

const errorText = (e) => (typeof e === "object" && e !== null ? (e.message || JSON.stringify(e)) : String(e));
const isTimeoutError = (text) => /timed out|timeout|Таймаут/i.test(text);
// The session or the device is gone (sleep, USB reset, agent restart): reconnecting helps.
const isLostSessionError = (text) => /NO_DEVICE|WebSocket|Ошибка соединения|not connected|closed|socket/i.test(text);

// Operators see plain instructions; the raw agent message still goes to the point logs.
function friendlyScannerError(raw) {
  const text = String(raw || "");
  if (text.includes("NO_DEVICE")) return "Сканер не найден. Проверьте, что он включён, и переподключите USB-кабель — программа подключится сама.";
  if (isTimeoutError(text)) return "Программа сканера не отвечает — «ВсеСкан» перезапускает её сама. Повторите через минуту; если не поможет — перезагрузите компьютер.";
  if (/Ошибка соединения|ECONNREFUSED|WebSocket/i.test(text)) return "Программа сканера не запущена. Подождите минуту — она запустится сама; если нет — перезагрузите компьютер.";
  return text;
}

// The SDK silently abandons every pending call when the socket closes (initConnectState() clears
// their timeouts without settling them), so an init or scan in flight would hang forever. Each
// session gets a signal that rejects once it is lost; calls are raced against it.
const sessionLost = new WeakMap();
const SESSION_LOST = "Соединение со сканером прервано (WebSocket closed)";

function trackSession(scan) {
  let abort;
  const promise = new Promise((_, reject) => { abort = () => reject(new Error(SESSION_LOST)); });
  promise.catch(() => { /* observed through guard() */ });
  sessionLost.set(scan, { promise, abort });
}

function abortSession(scan) {
  if (scan) sessionLost.get(scan)?.abort();
}

function guard(scan, promise) {
  const lost = sessionLost.get(scan)?.promise;
  return lost ? Promise.race([promise, lost]) : promise;
}

// close() waits for the socket's onclose, which a hung agent may never deliver.
function closeQuietly(scan) {
  if (!scan) return Promise.resolve();
  return withTimeout(scan.close(), 3000, "close timeout").catch(() => { /* already closed or hung */ });
}

function withTimeout(promise, ms, message) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(message)), ms)),
  ]);
}

function rotateBase64(base64, deg) {
  if (!deg) return Promise.resolve(base64);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const swap = deg === 90 || deg === 270;
      const canvas = document.createElement("canvas");
      canvas.width  = swap ? img.naturalHeight : img.naturalWidth;
      canvas.height = swap ? img.naturalWidth  : img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((deg * Math.PI) / 180);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      resolve(canvas.toDataURL("image/jpeg", 0.92));
    };
    img.src = base64;
  });
}

const ScannerContext = createContext(null);

export function ScannerProvider({ children }) {
  const [ip, setIp] = useState(DEFAULT_IP);
  const [port, setPort] = useState(DEFAULT_PORT);
  const [status, setStatus] = useState("disconnected");
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [deviceOptions, setDeviceOptions] = useState(null);
  const [resolution, setResolution] = useState(150);
  const [paperSize, setPaperSize] = useState("A3");
  const [orientation, setOrientation] = useState("landscape");
  // Feet are now always placed on the bed the same way — this default rotation lands the raw
  // scan vertical (toes up) without the operator needing to touch the manual rotate button.
  // Was 180 — that landed vertical but upside down (180° off), so corrected to 0.
  const [imageRotate, setImageRotate] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [scanCount, setScanCount] = useState(0);
  const [error, setError] = useState("");
  // Re-arms the auto-connect timer after every failure: a failure that leaves the status at
  // "disconnected" (a session lost while a connect was still running) would otherwise not
  // re-run the effect, and auto-connect would stop for good.
  const [retryTick, setRetryTick] = useState(0);
  const bumpRetry = () => setRetryTick((t) => t + 1);

  // Advanced settings
  const [source, setSource] = useState("Flatbed");
  const [colorMode, setColorMode] = useState("color");
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [quality, setQuality] = useState(80);
  const [swcrop, setSwcrop] = useState(false);
  const [swdeskew, setSwdeskew] = useState(false);
  const [removeBlankPage, setRemoveBlankPage] = useState(false);
  const [removeBlackEdges, setRemoveBlackEdges] = useState(false);
  const [denoise, setDenoise] = useState(false);
  const [borderFill, setBorderFill] = useState(false);
  const [removePunchHole, setRemovePunchHole] = useState(false);
  const [multiFeed, setMultiFeed] = useState(false);
  const [sharpen, setSharpen] = useState(false);
  const [autoRotate, setAutoRotate] = useState(false);

  const scanRef = useRef(null);
  const autoConnectRef = useRef(IS_ELECTRON);
  const connectRef = useRef(null);
  const dropSessionRef = useRef(null);
  const autoAttemptsRef = useRef(0);
  // Read by async flows (scan retry, health check, wake-up) that outlive the render they started in.
  const statusRef = useRef("disconnected");
  const scanningRef = useRef(false);
  const selectedDeviceRef = useRef("");
  const connectPromiseRef = useRef(null);
  const failStreakRef = useRef(0);

  const updateStatus = (value) => { statusRef.current = value; setStatus(value); };
  const updateSelectedDevice = (name) => { selectedDeviceRef.current = name; setSelectedDevice(name); };

  useEffect(() => {
    return () => { scanRef.current?.close().catch(() => {}); };
  }, []);

  const applyDeviceDefaults = (opts) => {
    const res = opts?.resolution?.value;
    const rt = opts?.resolution?.type;
    if (res && Array.isArray(res)) {
      const isCurrentValid = rt === "range"
        ? resolution >= (res[0] || 0) && resolution <= res[1]
        : res.includes(resolution);
      if (!isCurrentValid) {
        if (rt === "range") {
          const [min, max] = res;
          const common = [75, 100, 150, 200, 300, 400, 600, 1200].filter(v => v <= max && v >= (min || 75));
          const preferred = common.includes(150) ? 150 : (common.includes(100) ? 100 : (common[0] ?? max));
          setResolution(preferred);
        } else {
          const preferred = res.includes(150) ? 150 : (res.includes(100) ? 100 : res[0]);
          setResolution(preferred);
        }
      }
    }
    const ps = opts?.paperSize?.value;
    if (ps && Array.isArray(ps) && !ps.includes(paperSize)) {
      setPaperSize(ps.includes("A3") ? "A3" : ps[0]);
    }
    const sourceOpts = opts?.source?.value;
    if (sourceOpts && Array.isArray(sourceOpts) && !sourceOpts.includes(source)) {
      setSource(sourceOpts[0]);
    }
  };

  const clearDevice = () => {
    setDevices([]);
    updateSelectedDevice("");
    setDeviceOptions(null);
  };

  // Forget the current agent session (dead socket, vanished device, wake-up) so the next scan or
  // auto-connect starts a fresh one instead of talking to a session that no longer works.
  const dropSession = () => {
    const stale = scanRef.current;
    scanRef.current = null;
    abortSession(stale);
    stale?.close().catch(() => { /* already closed */ });
    autoAttemptsRef.current = 0;
    clearDevice();
    updateStatus("disconnected");
    bumpRetry();
  };

  const restartAgent = (reason) => {
    api.restartScannerAgent(reason).catch(() => { /* not logged in yet or a dev backend; retried on the next failures */ });
  };

  const connectOnce = async (currentIp, currentPort) => {
    const scan = new WebFxScan({ mode: "dev" });
    scanRef.current = scan;
    trackSession(scan);
    // Callbacks of a replaced session must not touch the state of the current one: closing a
    // stale session used to flip a fresh connection back to "disconnected".
    const lost = () => {
      abortSession(scan);
      if (scanRef.current !== scan) return;
      // A working session that just dropped: reconnect quickly, not after the 15 s retry delay.
      if (statusRef.current === "connected") autoAttemptsRef.current = 0;
      scanRef.current = null;
      clearDevice();
      updateStatus("disconnected");
      bumpRetry();
    };
    await withTimeout(
      scan.connect({
        ip: currentIp, port: currentPort,
        errorCallback: () => {
          if (scanRef.current !== scan) return;
          clientLog.error("scanner", "Ошибка соединения со сканером (WebScan2)", { ip: currentIp, port: currentPort });
          setError(friendlyScannerError("Ошибка соединения"));
          lost();
        },
        closeCallback: () => {
          if (scanRef.current !== scan) return;
          clientLog.warn("scanner", "Соединение со сканером прервано", { ip: currentIp, port: currentPort });
          lost();
        },
        eventCallback: () => {},
        ipExceptionCallback: () => {},
      }),
      CONNECT_TIMEOUT_MS,
      "Таймаут подключения — проверьте IP/порт и что агент сканера запущен"
    );
    await guard(scan, scan.init());
    const result = await guard(scan, scan.getDeviceList());
    if (scanRef.current !== scan) throw new Error(SESSION_LOST);
    const opts = result.data?.options || [];
    if (!opts.length) {
      // Keep retrying instead of showing "connected" without a device to scan with.
      clientLog.warn("scanner", "Агент сканера доступен, но устройство не найдено", { ip: currentIp, port: currentPort });
      scanRef.current = null;
      await closeQuietly(scan);
      throw new Error(NO_DEVICES);
    }
    setDevices(opts);
    updateSelectedDevice(opts[0].deviceName);
    setDeviceOptions(opts[0]);
    applyDeviceDefaults(opts[0]);
    clientLog.info("scanner", `Сканер подключён: ${opts[0].deviceName}`, { devices: opts.map((d) => d.deviceName) });
    failStreakRef.current = 0;
    updateStatus("connected");
  };

  const reportConnectError = (raw, details) => {
    // NO_DEVICES is already logged as a warning in connectOnce.
    if (raw !== NO_DEVICES) clientLog.error("scanner", `Не удалось подключиться к сканеру: ${raw}`, details);
    updateStatus("disconnected");
    bumpRetry();
    setError(friendlyScannerError(raw));
    // A hung agent (port open, init never answers) used to need a full app restart.
    if (!IS_ELECTRON) return;
    failStreakRef.current += 1;
    if (failStreakRef.current >= (raw === NO_DEVICES ? AGENT_RESTART_AFTER_NO_DEVICES : AGENT_RESTART_AFTER)) {
      failStreakRef.current = 0;
      restartAgent(raw);
    }
  };

  const connectNow = async () => {
    setError("");
    updateStatus("connecting");
    // Close any stale connection first
    { const stale = scanRef.current; scanRef.current = null; abortSession(stale); await closeQuietly(stale); }
    try {
      await connectOnce(ip, port);
    } catch (e) {
      const msg = errorText(e);
      // If agent reports stale session, wait and retry once
      if (msg.toLowerCase().includes("already")) {
        try {
          await new Promise(r => setTimeout(r, 800));
          { const stale = scanRef.current; scanRef.current = null; abortSession(stale); await closeQuietly(stale); }
          await connectOnce(ip, port);
        } catch (e2) {
          reportConnectError(errorText(e2), { ip, port, retry: true });
        }
      } else {
        reportConnectError(msg, { ip, port });
      }
    }
  };

  // One connect at a time: auto-connect, wake-up and a scan waiting for the scanner share it.
  const connect = () => {
    autoConnectRef.current = IS_ELECTRON;
    if (!connectPromiseRef.current) {
      connectPromiseRef.current = connectNow().finally(() => { connectPromiseRef.current = null; });
    }
    return connectPromiseRef.current;
  };

  const isReady = () => !!(scanRef.current && statusRef.current === "connected" && selectedDeviceRef.current);

  const ensureConnected = async () => {
    if (isReady()) return true;
    if (!autoConnectRef.current) return false;
    await connect();
    return isReady();
  };

  // Desktop app: connect on start and keep retrying while disconnected (agent still starting,
  // scanner switched off or replugged), so operators never have to press "Подключить".
  // A deliberate "Отключить" stops it until the next manual connect.
  useEffect(() => { connectRef.current = connect; dropSessionRef.current = dropSession; });
  useEffect(() => {
    if (!autoConnectRef.current || status !== "disconnected") return undefined;
    const delay = autoAttemptsRef.current === 0 ? 1500 : AUTO_CONNECT_RETRY_MS;
    const timer = setTimeout(() => {
      if (!autoConnectRef.current || connectPromiseRef.current) return;
      autoAttemptsRef.current += 1;
      connectRef.current();
    }, delay);
    return () => clearTimeout(timer);
  }, [status, retryTick]);

  // A session can look connected while the scanner is gone (USB reset, scanner power-saving):
  // ask the agent for its devices once a minute and reconnect when the scanner is missing.
  useEffect(() => {
    if (!IS_ELECTRON || status !== "connected") return undefined;
    const timer = setInterval(async () => {
      const scan = scanRef.current;
      if (!scan || scanningRef.current || connectPromiseRef.current) return;
      let reason = null;
      try {
        const r = await withTimeout(guard(scan, scan.getDeviceList()), HEALTH_CHECK_TIMEOUT_MS, "Connection timed out: getDeviceList (проверка сканера)");
        if (!(r.data?.options || []).length) reason = NO_DEVICES;
      } catch (e) {
        reason = errorText(e);
      }
      if (!reason || scanRef.current !== scan || scanningRef.current) return;
      clientLog.warn("scanner", "Сканер перестал отвечать — переподключение", { reason });
      dropSessionRef.current();
    }, HEALTH_CHECK_MS);
    return () => clearInterval(timer);
  }, [status]);

  // main.cjs dispatches this a few seconds after the computer wakes up: the old session rarely
  // survives sleep, so start a fresh one before the operator presses "Сканировать".
  useEffect(() => {
    if (!IS_ELECTRON) return undefined;
    const onResume = () => {
      if (!autoConnectRef.current || scanningRef.current || connectPromiseRef.current) return;
      clientLog.info("scanner", "Переподключение сканера после сна компьютера", { status: statusRef.current });
      if (statusRef.current === "connected") dropSessionRef.current();
      else connectRef.current();
    };
    window.addEventListener("scanapp:resume", onResume);
    return () => window.removeEventListener("scanapp:resume", onResume);
  }, []);

  const disconnect = async () => {
    autoConnectRef.current = false;
    const current = scanRef.current;
    scanRef.current = null;
    abortSession(current);
    await closeQuietly(current);
    updateStatus("disconnected");
    clearDevice();
  };

  const handleDeviceChange = (name) => {
    updateSelectedDevice(name);
    const opts = devices.find(d => d.deviceName === name);
    setDeviceOptions(opts || null);
    if (opts) applyDeviceDefaults(opts);
  };

  const runScan = async (overrides, collected) => {
    const effectivePaperSize = (paperSize === "auto") ? null : paperSize;
    const effectiveOrientation = (effectivePaperSize === "A3") ? "portrait" : orientation;
    const { colorMode: overrideColorMode, ...restOverrides } = overrides;
    const scannerParams = {
      deviceName: selectedDeviceRef.current,
      source,
      resolution: Number(resolution),
      mode: overrideColorMode || colorMode,
      paperOrientation: effectiveOrientation,
      base64enc: true,
      brightness: Number(brightness),
      contrast: Number(contrast),
      quality: Number(quality),
      swcrop,
      swdeskew,
      removeBlankPage,
      removeBlackEdges,
      denoise,
      borderFill,
      removePunchHole,
      multiFeed,
      sharpen,
      autoRotate,
      ...restOverrides,
    };
    if (effectivePaperSize) scannerParams.paperSize = effectivePaperSize;
    const scan = scanRef.current;
    await guard(scan, scan.setScanner(scannerParams));
    await guard(scan, scan.scan({
      callback: ({ fileName, base64, status: st }) => {
        if (st === "scanning") {
          collected.push({ fileName, base64 });
          setScanCount(c => c + 1);
        }
      },
    }));
  };

  const doScan = async (onNewPages, overrides = {}) => {
    if (scanningRef.current) return;
    setError("");
    scanningRef.current = true;
    setScanning(true);
    setScanCount(0);
    const collected = [];
    const logDetails = () => ({ deviceName: selectedDeviceRef.current, resolution, paperSize, source, colorMode });
    try {
      if (!(await ensureConnected())) {
        clientLog.warn("scanner", "Сканирование не начато: сканер не подключён", { status: statusRef.current, auto: autoConnectRef.current });
        setError(autoConnectRef.current
          ? "Сканер не подключён. Проверьте, что он включён, — программа продолжает подключаться сама."
          : "Сканер не подключён. Перейдите в Настройки.");
        return;
      }
      try {
        await runScan(overrides, collected);
      } catch (e) {
        const msg = errorText(e);
        // A session lost to sleep or a USB reset: reconnect and scan once more, unless pages
        // already arrived (nothing is lost then) or the agent hung (a timeout — it is restarted).
        if (collected.length || !autoConnectRef.current || isTimeoutError(msg) || !isLostSessionError(msg)) throw e;
        clientLog.warn("scanner", `Сканирование прервалось — переподключение и повтор: ${msg}`, logDetails());
        dropSession();
        if (!(await ensureConnected())) throw e;
        await runScan(overrides, collected);
        clientLog.info("scanner", "Повторное сканирование после переподключения выполнено", logDetails());
      }
      if (collected.length) {
        const deg = Number(imageRotate);
        const rotated = deg
          ? await Promise.all(collected.map(async p => ({ ...p, base64: await rotateBase64(p.base64, deg) })))
          : collected;
        onNewPages(rotated, Number(resolution));
        clientLog.info("scanner", `Сканирование выполнено: ${collected.length} стр.`, { deviceName: selectedDeviceRef.current, resolution, paperSize, source });
      } else {
        clientLog.warn("scanner", "Сканирование завершилось без страниц", { deviceName: selectedDeviceRef.current, resolution, paperSize, source });
      }
    } catch (e) {
      const msg = errorText(e);
      clientLog.error("scanner", `Ошибка сканирования: ${msg}`, logDetails());
      setError(friendlyScannerError(msg));
      if (IS_ELECTRON && autoConnectRef.current && (isTimeoutError(msg) || isLostSessionError(msg))) {
        dropSession();
        if (isTimeoutError(msg)) restartAgent(`scan: ${msg}`);
      }
    } finally {
      scanningRef.current = false;
      setScanning(false);
      setScanCount(0);
    }
  };

  const resolutionOptions = (() => {
    const rv = deviceOptions?.resolution?.value;
    const rt = deviceOptions?.resolution?.type;
    if (!rv) return [100, 150, 200, 300, 600];
    if (rt === "range") {
      const common = [75, 100, 150, 200, 300, 400, 600, 1200].filter(v => v <= rv[1] && v >= (rv[0] || 75));
      return common.length ? common : [rv[1]];
    }
    return rv;
  })();

  const paperSizeOptions = ["auto", ...(deviceOptions?.paperSize?.value || ["A3", "A4", "A5", "A6", "B5", "Letter"])];

  return (
    <ScannerContext.Provider value={{
      ip, setIp, port, setPort,
      status, devices, selectedDevice, deviceOptions,
      setSelectedDevice: handleDeviceChange,
      resolution, setResolution,
      paperSize, setPaperSize,
      orientation, setOrientation,
      imageRotate, setImageRotate,
      scanning, scanCount, error,
      connect, disconnect,
      scan: doScan,
      resolutionOptions, paperSizeOptions,
      source, setSource,
      colorMode, setColorMode,
      brightness, setBrightness,
      contrast, setContrast,
      quality, setQuality,
      swcrop, setSwcrop,
      swdeskew, setSwdeskew,
      removeBlankPage, setRemoveBlankPage,
      removeBlackEdges, setRemoveBlackEdges,
      denoise, setDenoise,
      borderFill, setBorderFill,
      removePunchHole, setRemovePunchHole,
      multiFeed, setMultiFeed,
      sharpen, setSharpen,
      autoRotate, setAutoRotate,
    }}>
      {children}
    </ScannerContext.Provider>
  );
}

export const useScanner = () => useContext(ScannerContext);
